import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_EMAIL, FIELD_FIRST_NAME, FIELD_LAST_NAME } from '@components/dynamic-input-fields/src/utils';
import { SelectType } from '@components/forms/box-office/select-type';
import { createFieldsFromFormFields } from '@components/forms/utils/create-fields-from-form-fields';
import { IconDownload, IconPrint } from '@components/icons';
import { SummaryFormModalContent, SummarySuccessModalContent } from '@components/modals';
import { FormField, FormValue } from '@components/modals/add-holder-modal/src/add-holder-modal.type';
import { DATE_FORMAT_SHORT_DATE } from '@constants/datetime';
import { XS_VIEWPORT } from '@constants/viewport';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { EventContext } from '@contexts/event/event-context';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { ModalContext } from '@contexts/modal/modal-context';
import { Admission, BOOKING_FORM_FIELDS } from '@domain/admission';
import { ROLE_PROMOTER, ROLE_TEAM_LEADER } from '@domain/company';
import { useGetAdmission } from '@hooks/admissions/use-get-admission';
import { useCreateBoxOffice } from '@hooks/box-office/use-create-box-office';
import { useGetEvent } from '@hooks/events/use-get-event';
import { useWindowSize } from '@hooks/use-window-size';
import { downloadFile } from '@pages/events/[companyId]/[clubSlug]/customers/utils';
import { handlePdf } from '@utils/download-file';
import moment from 'moment';

import { PrintModal } from '../components/print-modal';
import { SuccessMessage } from '../components/success-message';

import {
  BoxOfficeForm,
  ProcessBoxOfficeStep,
  STEP_INFO,
  STEP_SELECT_TYPE,
  STEP_SUCCESS,
} from './process-box-office.type';

export const ProcessBoxOffice = () => {
  const { t } = useTranslation();
  const mobileViewport = useWindowSize(XS_VIEWPORT);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    eventState: { id: paramEventId, isLiveEvent },
  } = useContext(EventContext);
  const { isWebView } = useContext(FeatureFlagContext);
  const { toggleModal, setPreventExit, handleCloseModal } = useContext(ModalContext);
  const { role } = useContext(CompanyAuthContext);
  const {
    companyState: {
      defaultChannel: { id: defaultChannelId },
    },
  } = useContext(CompanyClubContext);

  const isTeamLeaderOrPromoter = role && (role.name === ROLE_TEAM_LEADER || role.name === ROLE_PROMOTER);

  const [step, setStep] = useState<ProcessBoxOfficeStep>(STEP_SELECT_TYPE);
  const [pdfFile, setPdfFile] = useState<string | Blob>('');
  const [bookingId, setBookingId] = useState<string>('');
  const [isOpenPdfFileModal, setIsOpenPdfFileModal] = useState<boolean>(false);
  const methods = useForm<BoxOfficeForm>({
    defaultValues: {
      eventId: isLiveEvent ? paramEventId ?? '' : '',
      offerId: '',
      channelId: isTeamLeaderOrPromoter ? '' : defaultChannelId,
      quantity: 1,
      bookingFormFields: {},
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
  } = methods;

  const offerId = watch('offerId');
  const quantity = watch('quantity');
  const eventId = watch('eventId');
  const channelId = watch('channelId');
  const email = watch('bookingFormFields.email');
  const { data: offer } = useGetAdmission({
    offerId,
    channelId,
    options: {
      enabled: !!offerId,
    },
  });
  const { data: event } = useGetEvent({ eventId });

  const { data: boxOfficeData, mutate: boxOfficeMutation, isSuccess: isBoxOfficeSuccess } = useCreateBoxOffice();

  const createBoxOffice = (formValue: FormValue) => boxOfficeMutation({ formValue, isWebView });

  const closeModal = useCallback(() => toggleModal({ isOpen: false, type: '' }), [toggleModal]);

  const onSubmit = (e: BoxOfficeForm) => {
    createBoxOffice({
      offerId: e.offerId,
      bookingFormFields: createFieldsFromFormFields<BoxOfficeForm>(e, BOOKING_FORM_FIELDS) as FormField[],
      numTickets: e.quantity,
      channelId: e.channelId,
    });
  };
  const newBoxOfficeProcess = () => {
    setStep(STEP_SELECT_TYPE);
  };

  const boxOfficeFields = [FIELD_FIRST_NAME, FIELD_LAST_NAME, FIELD_EMAIL];
  const filteredBookingFormFields = (admission: Admission) =>
    admission.bookingFormFields.filter((field) => boxOfficeFields.includes(field.slug));
  const filteredOffer = (admission: Admission) => ({
    ...admission,
    bookingFormFields: filteredBookingFormFields(admission),
  });

  const handleFile = useCallback(
    (file: string | Blob, filename?: string) => {
      if (isWebView) {
        const time = moment().format(DATE_FORMAT_SHORT_DATE);
        return downloadFile(file as Blob, 'pdf', filename ? filename : `box-office-${offerId}-${time}`);
      }
      if (mobileViewport) return handlePdf({ file: file as string, name: 'box-office', id: bookingId });
      return setIsOpenPdfFileModal(true);
    },
    [mobileViewport, bookingId, isWebView, offerId]
  );

  const handleSecondarySuccessAction = () => {
    handleFile(pdfFile, boxOfficeData?.filename);
  };

  useEffect(() => {
    if (isBoxOfficeSuccess && boxOfficeData) {
      const file = isWebView ? boxOfficeData.blob : boxOfficeData.file;
      const filename = boxOfficeData.filename;
      setPdfFile(file);
      setBookingId(isWebView ? '' : boxOfficeData.id);
      setStep(STEP_SUCCESS);
      setPreventExit(false);
      handleFile(file, filename);
    }
  }, [isWebView, isBoxOfficeSuccess, boxOfficeData, handleFile, setPreventExit]);

  useEffect(() => {
    setPreventExit(isDirty);
  }, [isDirty, setPreventExit]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        {step === STEP_SELECT_TYPE && <SelectType onContinue={() => setStep(STEP_INFO)} offer={offer} />}
        {step === STEP_INFO && offer && event && (
          <SummaryFormModalContent
            event={event}
            offer={filteredOffer(offer)}
            quantity={quantity}
            onContinue={handleSubmit(onSubmit)}
            optionalFields
            handleClose={handleCloseModal}
            title={t('offers:fillInfoBoxOfficeTitle')}
            mainActionText={email ? t('offers:printAndSend') : t('offers:print')}
            mainActionIcon={IconPrint}
          />
        )}
        {step === STEP_SUCCESS && offer && (
          <SummarySuccessModalContent
            offer={offer}
            quantity={quantity}
            onClickMainAction={newBoxOfficeProcess}
            onClickSecondaryAction={handleSecondarySuccessAction}
            onClickClose={closeModal}
            title={t('offers:processBoxOfficeSuccessTitle')}
            headerDescription={<SuccessMessage bookingLegacyId={boxOfficeData?.legacyId} email={email} />}
            bookingLegacyId={boxOfficeData?.legacyId}
            mainActionText={t('offers:addGuest')}
            secondaryActionText={mobileViewport ? t('offers:downloadPdf') : t('offers:printAgain')}
            secondaryActionIcon={mobileViewport ? IconDownload : IconPrint}>
            {pdfFile && !isWebView && !mobileViewport ? (
              <PrintModal
                isOpen={isOpenPdfFileModal}
                toggleModal={setIsOpenPdfFileModal}
                isMobile={mobileViewport}
                file={pdfFile as string}
              />
            ) : undefined}
          </SummarySuccessModalContent>
        )}
      </FormProvider>
    </form>
  );
};
